//#region src/slug-grammar.ts
/**
* ⭐ **THE RESOURCE-SLUG GRAMMAR — the ONE definition, in the LEAF package.**
*
* ## ⛔ Why this module exists (#1036)
*
* `/^[a-z0-9][a-z0-9-]*[a-z0-9]$/` was declared SIX times: here's-where-it-was as
* `app-admin-inputs.SLUG_PATTERN`, inline in `oauth.ts`'s `clientId`, and once each in
* `cp-admin-ui`, `platform-api`, `dp/shell` and `cp-auth-api` (that last one as a string). All six
* were character-identical and NOTHING kept them so.
*
* ⛔ The one-declaration rule was already written down and five sites broke it:
* `app-admin-inputs.SLUG_PATTERN` says *"ONE declaration, platform-wide (#1107) … do not
* re-declare it, and do not re-type the literal anywhere."* One violator was 460 lines away in the
* same package.
*
* ⭐ And the copy that mattered most ADMITTED it was a copy: `cp-admin-ui`'s `TENANT_SLUG_PATTERN`
* said it was *"Copied rather than imported because platform-api is a service, not a published
* contract this package may depend on"*, and paid for the copy with a test that read the producer's
* SOURCE TEXT across a package boundary. That obstacle was real and it disappears here: the
* grammar lives in the leaf package both sides already depend on, so the source-text read becomes a
* `toBe`.
*
* ## ⛔ WHY A NEW NAME RATHER THAN REUSING `SLUG_PATTERN`
*
* `SLUG_PATTERN`'s docblock scopes it to *"the app-slug grammar"*. Once tenant slugs, org slugs,
* OAuth client ids and machine-principal slugs all reference it, that sentence is a lie. The
* grammar gets a name that says what it governs; each DOMAIN keeps its own name as an alias, so a
* call site still reads in the vocabulary of its own domain.
*
* ## ⛔ WHAT THIS DELIBERATELY DID **NOT** DO
*
* It did not make the near-miss grammars agree. Three sites use
* `/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/`, which accepts a ONE-character slug this one rejects, and two
* use `/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/`, which rejects a TWO-character slug and a 65-character
* slug this one accepts. Measured, all three, by execution. Reconciling them would change what a
* live endpoint accepts, which is a product decision. `__tests__/slug-grammar.test.ts` PINS all
* three languages instead, so the next person to touch one sees the difference in a diff.
*/
/**
* A lowercase resource slug: alphanumeric ends, hyphens permitted in the middle.
*
* ⭐ **Read the shape before changing it: `first-char` + `middle*` + `last-char`, so it requires TWO
* characters minimum.** That is not obvious at a glance and it is the single most-misread property
* of this pattern.
*
* ⚠️ It PERMITS consecutive hyphens (`a--b` matches). That is what separates it from
* {@link ID_PART_PATTERN}, which forbids them because `--` is the instance-key delimiter. Do not
* "unify" the two: `id-grammar.ts` records the measured reason a second copy was refused and the
* measured reason these two are not interchangeable.
*
* ⚠️ Anchored at both ends, no unbounded nested quantifier, and NOT `/g` flagged (`.test()` on a
* `/g` regex is stateful across calls and would alternate on identical input).
*
* ⛔ **APPLY A LENGTH BOUND BEFORE THIS PATTERN — and that is guidance, not a description of the
* tree.** An earlier draft of this docblock asserted that *every* consumer already writes
* `.max(...)` before `.regex(...)`. MEASURED and false: `platform-api`'s `createOrgSchema` binds
* `.string().min(1).regex(SLUG_PATTERN)` with no upper bound. Stating it as an accomplished fact
* is worse than stating it as a rule, because a reader checking whether the rule holds would
* stop at the comment. ⚠️ The unbounded site is recorded rather than silently fixed: adding a
* `.max` to a live create-org door changes what that endpoint accepts, which is out of this
* change's fence.
*
* ⛔ Consumers import THIS OBJECT. A re-typed literal with the same source is `!==`, so a test
* comparing by `.source` passes while the two drift independently on the next edit. Every alias is
* pinned by `toBe`.
*/
const RESOURCE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
//#endregion
export { RESOURCE_SLUG_PATTERN };

//# sourceMappingURL=slug-grammar.js.map